import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import useUploadEml from '@/features/analysis/mutations/useUploadEml';
import useCreateDocumentForDistribution from '@/features/documents/mutations/useCreateDocumentForDistribution';
import documentQueryOptions from '@/features/documents/queries/documentQueryOptions';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import { buildEmlFilename } from '@/lib/email/buildEmlFilename';

type EmailDocumentRef = {
  filename: string;
  link: string;
};

export default function useEnsureEmailDocument() {
  const {
    email,
    analysis: { email: analyzedEmail, emailDocument },
  } = useEmail();

  const queryClient = useQueryClient();
  const { getEmlBlob } = useEmailClient();

  const createDocumentForDistribution = useCreateDocumentForDistribution();
  const uploadEml = useUploadEml();

  return useCallback(async (): Promise<EmailDocumentRef> => {
    if (emailDocument) {
      return { filename: emailDocument.filename, link: emailDocument.link };
    }

    let documentId = analyzedEmail?.document_id;

    if (!documentId) {
      const { document_id, link: uploadLink } =
        await createDocumentForDistribution.mutateAsync({
          filename: buildEmlFilename(email?.subject),
          description: email?.messageId ?? '',
          tags: ['MAIL'],
          isHiddenForCustomer: true,
          type: 'OTHER',
        });

      const eml = await getEmlBlob();
      await uploadEml.mutateAsync({ link: uploadLink, blob: eml });

      documentId = document_id;
    }

    const metadata = await queryClient.ensureQueryData(
      documentQueryOptions(documentId),
    );

    return { filename: metadata.filename, link: metadata.link };
  }, [
    analyzedEmail?.document_id,
    createDocumentForDistribution,
    email?.messageId,
    email?.subject,
    emailDocument,
    getEmlBlob,
    queryClient,
    uploadEml,
  ]);
}
